import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleCheck,
  faChevronLeft,
  faChartColumn,
} from '@fortawesome/free-solid-svg-icons'
import { Lock, Plus } from '@phosphor-icons/react'
import GrowthWheelWorks from '../../components/GrowthWheelWorks/GrowthWheelWorks'
import GrowthWheelGraph from '../../components/GrowthWheelVisualizer/growth-wheel-graph'
import CompetencyCard from '../../components/GrowthWheelComponents/CompetencyCard'
import {
  evaluateFocusCompetenciesLimit,
  evaluateGrowthWheelCompletion,
  setAssessmentCompletion,
  setAssessmentSession,
  setSelectedAssessmentSessionId,
  setCurrentCompetency,
  setCurrentAreasOfFocus,
  setCurrentSubCompetency,
  setUserCompetenciesData,
} from '../../redux/growthWheel.slice'
import { RootState } from '../../redux/store'
import { Competency } from '../../types/GrowthWheel'
import {
  getSelfCompletedCompetencies,
  getGuidedCompletedCompetencies,
  getTotalCompetencies,
} from '../../utils/growth-wheel-utils'
import AreasOfFocus from '../../components/GrowthWheelComponents/AreasOfFocus'
import {
  useCompleteGrowthWheelAssessmentMutation,
  useStartGrowthWheelAssessmentMutation,
} from '../../redux/growth-wheel.endpoints'
import { MarkCompletedModal } from '../../components/GrowthWheelComponents/MarkCompletedModal'
import { StartAssessmentModal } from '../../components/GrowthWheelComponents/StartAssessmentModal'
import { SessionSelector } from '../../components/GrowthWheelComponents/SessionSelector'
import { GrowthComparisonChart } from '../../components/GrowthWheelComponents/GrowthComparisonChart'

interface Props {
  isAdminView?: boolean
  userId?: number | null
  userName?: string
  guildName?: string
}

const GrowthWheelDashboard = ({
  isAdminView = false,
  userId,
  userName,
  guildName,
}: Props) => {
  const dispatch = useDispatch()
  const [showMarkCompletedModal, setShowMarkCompletedModal] = useState(false)
  const [showStartAssessmentModal, setShowStartAssessmentModal] =
    useState(false)
  const [showCompareGrowth, setShowCompareGrowth] = useState(false)
  const [completionError, setCompletionError] = useState('')
  const [completeGrowthWheelAssessment, { isLoading: isCompleting }] =
    useCompleteGrowthWheelAssessmentMutation()
  const [startGrowthWheelAssessment, { isLoading: isStartingAssessment }] =
    useStartGrowthWheelAssessmentMutation()
  const growthWheelData = useSelector(
    (state: RootState) => state.growthWheel.growthWheelData,
  )
  const isLoading = useSelector(
    (state: RootState) => state.growthWheel.isLoadingGrowthWheel,
  )
  const userCompetenciesData = useSelector(
    (state: RootState) => state.growthWheel.userCompetenciesData,
  )
  const growthWheelCompleted = useSelector(
    (state: RootState) => state.growthWheel.growthWheelCompleted,
  )
  const assessmentCompleted = useSelector(
    (state: RootState) => state.growthWheel.assessmentCompleted,
  )
  const assessmentSession = useSelector(
    (state: RootState) => state.growthWheel.assessmentSession,
  )
  const isReadOnly = useSelector(
    (state: RootState) => state.growthWheel.isReadOnly,
  )
  const assessmentSessions = useSelector(
    (state: RootState) => state.growthWheel.assessmentSessions,
  )
  const selectedAssessmentSessionId = useSelector(
    (state: RootState) => state.growthWheel.selectedAssessmentSessionId,
  )
  const activeSessionId = useSelector(
    (state: RootState) => state.growthWheel.activeAssessmentSessionId,
  )
  const activeGrowthWheelId = useSelector(
    (state: RootState) => state.growthWheel.activeGrowthWheelId,
  )
  const sessionOptions =
    assessmentSession &&
    !assessmentSessions.some(session => session.id === assessmentSession.id)
      ? [assessmentSession, ...assessmentSessions]
      : assessmentSessions
  const selectedSessionId =
    selectedAssessmentSessionId ??
    activeSessionId ??
    sessionOptions[0]?.id ??
    assessmentSession?.id ??
    null
  const selectedSession =
    sessionOptions.find(session => session.id === selectedSessionId) ??
    assessmentSession ??
    null
  const isViewingActiveSession =
    activeSessionId != null && selectedSessionId === activeSessionId
  const allSubCompetencies = userCompetenciesData.flatMap(
    competency => competency.subCompetencies ?? [],
  )
  const incompleteCount = allSubCompetencies.filter(
    sub =>
      sub.evaluation?.selfLevel == null || sub.evaluation?.leaderLevel == null,
  ).length
  const canMarkCompleted =
    isAdminView &&
    isViewingActiveSession &&
    !isReadOnly &&
    !assessmentCompleted &&
    userId != null &&
    (assessmentSession?.growthWheelId ?? growthWheelData?.id) != null &&
    allSubCompetencies.length > 0 &&
    incompleteCount === 0
  const assessmentName =
    assessmentSession?.name || growthWheelData?.name || 'Growth Wheel'
  const canStartNewAssessment =
    isAdminView &&
    assessmentCompleted &&
    activeSessionId == null &&
    userId != null &&
    (activeGrowthWheelId ?? growthWheelData?.id) != null
  const isViewingHistoricalSession =
    selectedSession != null &&
    activeSessionId != null &&
    selectedSession.id !== activeSessionId

  const handleClickCompetency = (competency: Competency | null) => {
    dispatch(setCurrentCompetency(competency))
  }

  const ContinueAssessment = () => {
    const firstUncompletedSub = userCompetenciesData
      ?.flatMap(c => c.subCompetencies ?? [])
      .find(sub => !sub.evaluation?.selfLevel)
    if (firstUncompletedSub) {
      const parentCompetency = userCompetenciesData?.find(c =>
        c.subCompetencies?.some(s => s.id === firstUncompletedSub.id),
      )
      if (parentCompetency) {
        dispatch(setCurrentCompetency(parentCompetency))
        dispatch(setCurrentSubCompetency(firstUncompletedSub))
      }
    }
  }

  const shouldDisplayStartAssessment = userCompetenciesData
    ?.flatMap(c => c.subCompetencies ?? [])
    .every(sub => !sub.evaluation?.selfLevel)

  const handleConfirmCompleteAssessment = async () => {
    const completionGrowthWheelId =
      assessmentSession?.growthWheelId ?? growthWheelData?.id

    if (
      !canMarkCompleted ||
      userId == null ||
      completionGrowthWheelId == null
    ) {
      return
    }

    try {
      const result = await completeGrowthWheelAssessment({
        growthWheelId: completionGrowthWheelId,
        userId,
      }).unwrap()
      dispatch(
        setAssessmentCompletion({
          isCompleted: result.isCompleted,
          completedAt: result.completedAt,
        }),
      )
      if (result.session) {
        dispatch(setAssessmentSession(result.session))
      }
      setShowMarkCompletedModal(false)
      setCompletionError('')
    } catch {
      setCompletionError('Could not complete the assessment. Please try again.')
    }
  }

  const handleConfirmStartAssessment = async (name: string) => {
    const newAssessmentGrowthWheelId =
      activeGrowthWheelId ?? growthWheelData?.id

    if (userId == null || newAssessmentGrowthWheelId == null) {
      return
    }

    try {
      const result = await startGrowthWheelAssessment({
        growthWheelId: newAssessmentGrowthWheelId,
        userId,
        name,
      }).unwrap()
      const clearedCompetencies = userCompetenciesData.map(competency => ({
        ...competency,
        subCompetencies: competency.subCompetencies?.map(subCompetency => ({
          ...subCompetency,
          evaluation: null,
        })),
      }))
      dispatch(setUserCompetenciesData(clearedCompetencies))
      dispatch(evaluateFocusCompetenciesLimit())
      dispatch(evaluateGrowthWheelCompletion())
      dispatch(setCurrentAreasOfFocus())
      dispatch(setCurrentCompetency(null))
      dispatch(setCurrentSubCompetency(null))
      dispatch(
        setAssessmentCompletion({
          isCompleted: result.isCompleted,
          completedAt: result.completedAt,
        }),
      )
      dispatch(setAssessmentSession(result.session))
      dispatch(setSelectedAssessmentSessionId(result.session.id))
      setShowStartAssessmentModal(false)
      setCompletionError('')
    } catch {
      setCompletionError('Could not start a new assessment. Please try again.')
    }
  }

  return (
    <div className="container mb-5 gw-growth-wheel-container">
      <div className="my-5">
        <div className="gw-session-bar mb-2">
          {sessionOptions.length > 0 && (
            <div className="gw-session-bar__selector">
              <span className="gw-session-bar__label">Assessment session:</span>
              <SessionSelector />
            </div>
          )}
          {userId != null && sessionOptions.length > 0 && (
            <div className="gw-session-bar__actions">
              <button
                type="button"
                onClick={() => setShowCompareGrowth(show => !show)}
                className="gw-compare-growth-toggle"
              >
                <FontAwesomeIcon icon={faChartColumn} />
                {showCompareGrowth ? 'Hide Comparison' : 'View Comparison'}
              </button>
            </div>
          )}
        </div>
        {completionError && (
          <div className="alert alert-danger small mt-3">{completionError}</div>
        )}
        {selectedSession?.status === 'completed' && (
          <div className="gw-assessment-locked-banner">
            <Lock
              size={16}
              weight="regular"
              className="gw-assessment-locked-banner__icon"
              aria-hidden
            />
            <div className="gw-assessment-locked-banner__text">
              Viewing completed assessment:{' '}
              <span className="gw-assessment-locked-banner__session-name">
                {selectedSession.name}
              </span>
              {selectedSession.completedAt ? (
                <span className="gw-assessment-locked-banner__date">
                  {' '}
                  · Completed{' '}
                  {new Date(selectedSession.completedAt).toLocaleDateString()}
                </span>
              ) : null}
              . All fields are locked.
            </div>
            {activeSessionId != null &&
              selectedSession.id !== activeSessionId && (
                <button
                  type="button"
                  onClick={() =>
                    dispatch(setSelectedAssessmentSessionId(activeSessionId))
                  }
                  className="gw-assessment-locked-banner__back-button"
                >
                  <FontAwesomeIcon icon={faChevronLeft} /> Back to current
                </button>
              )}
          </div>
        )}
        {isViewingHistoricalSession &&
          selectedSession?.status === 'in_progress' && (
            <div className="gw-assessment-locked-banner gw-assessment-locked-banner--historical">
              <Lock
                size={16}
                weight="regular"
                className="gw-assessment-locked-banner__icon"
                aria-hidden
              />
              <div className="gw-assessment-locked-banner__text">
                Viewing a historical session in read-only mode.
              </div>
              {activeSessionId != null && (
                <button
                  type="button"
                  onClick={() =>
                    dispatch(setSelectedAssessmentSessionId(activeSessionId))
                  }
                  className="gw-assessment-locked-banner__back-button"
                >
                  <FontAwesomeIcon icon={faChevronLeft} /> Back to current
                </button>
              )}
            </div>
          )}
        <div className="gw-growth-wheel-dashboard-header">
          <div>
            <h1 className="gw-growth-wheel-title">
              {userName ? `${userName}'s` : ''} Growth Wheel
            </h1>
            {guildName ? (
              <p className="gw-growth-wheel-guild-name">{guildName}</p>
            ) : null}
          </div>
          <div className="gw-dashboard-completion-action">
            {isAdminView && canStartNewAssessment ? (
              <button
                type="button"
                onClick={() => {
                  setCompletionError('')
                  setShowStartAssessmentModal(true)
                }}
                className="gw-btn-submit"
              >
                <Plus size={16} weight="regular" aria-hidden />
                Start New Assessment
              </button>
            ) : isAdminView &&
              !assessmentCompleted &&
              isViewingActiveSession ? (
              <>
                <div className="gw-completion-tooltip-wrapper">
                  <button
                    type="button"
                    onClick={() => {
                      setCompletionError('')
                      setShowMarkCompletedModal(true)
                    }}
                    disabled={!canMarkCompleted}
                    className="gw-btn-submit gw-btn-submit--success"
                  >
                    <FontAwesomeIcon icon={faCircleCheck} />
                    Mark as Completed
                  </button>
                  {!canMarkCompleted && (
                    <div className="gw-completion-tooltip">
                      {incompleteCount}{' '}
                      {incompleteCount === 1
                        ? 'competency is'
                        : 'competencies are'}{' '}
                      missing scores
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
        <p className="gw-growth-wheel-dashboard-tagline">
          Reflect on your competencies and prepare for meaningful growth
          conversations.
        </p>
      </div>
      {showCompareGrowth && userId != null && (
        <div className="mb-4">
          <GrowthComparisonChart
            userId={userId}
            sessions={sessionOptions}
            defaultSessionId={selectedSessionId}
            scoreField={isAdminView ? 'leaderLevel' : 'selfLevel'}
          />
        </div>
      )}
      <div className="row">
        <div className="col-10 col-md-4 gw-visualizer-container">
          {isLoading ? (
            <div>Loading...</div>
          ) : (
            <>
              <GrowthWheelGraph
                areas={userCompetenciesData.map(area => ({
                  id: area.id,
                  name: area.name,
                  subCompetencies: (area.subCompetencies ?? []).map(
                    competency => ({
                      id: competency.id,
                      name: competency.name,
                    }),
                  ),
                }))}
              />
              <GrowthWheelWorks />
            </>
          )}
        </div>
        <div className="col-12 col-md-8 gw-competency-area-cards-container">
          <div className="gw-competency-area-cards-header">
            <h3 className="gw-competency-area-cards-header-title">
              Competencies
            </h3>
            {!isAdminView &&
              !growthWheelCompleted &&
              !assessmentCompleted &&
              !isReadOnly && (
                <button
                  type="button"
                  onClick={ContinueAssessment}
                  className="gw-btn-submit"
                >
                  {shouldDisplayStartAssessment
                    ? 'Start Assessment'
                    : 'Continue Assessment'}
                </button>
              )}
          </div>
          <AreasOfFocus />
          <hr />
          <div className="gw-competency-area-list">
            {userCompetenciesData.map(competency => (
              <CompetencyCard
                key={competency.id}
                title={competency.name}
                description={competency.description}
                onClick={() => handleClickCompetency(competency)}
                selfCompleted={getSelfCompletedCompetencies(
                  competency.subCompetencies ?? [],
                )}
                guidedCompleted={getGuidedCompletedCompetencies(
                  competency.subCompetencies ?? [],
                )}
                total={getTotalCompetencies(competency.subCompetencies ?? [])}
              />
            ))}
          </div>
        </div>
      </div>
      <MarkCompletedModal
        show={showMarkCompletedModal}
        contributorName={userName || 'this contributor'}
        assessmentName={assessmentName}
        isSubmitting={isCompleting}
        onConfirm={handleConfirmCompleteAssessment}
        onCancel={() => setShowMarkCompletedModal(false)}
      />
      <StartAssessmentModal
        show={showStartAssessmentModal}
        contributorName={userName || 'this contributor'}
        isSubmitting={isStartingAssessment}
        onConfirm={handleConfirmStartAssessment}
        onCancel={() => setShowStartAssessmentModal(false)}
      />
    </div>
  )
}

export default GrowthWheelDashboard
